import { isStepUpError, rejectIfStepUp } from "../stepUp.ts";

export type GrantScope = "root" | "org" | "department" | "tag";

function rethrowStepUp(cause: unknown): void {
  if (isStepUpError(cause)) throw cause;
}

export type RoleGrant = {
  permission: string;
  scope: GrantScope;
  organisationId?: string;
  departmentId?: string;
  tagKey?: string;
  tagValue?: string;
};

export type MenuAccess = {
  roleLabel: string;
  canConnect: string;
  administer: boolean;
  connect: boolean;
  roles: string[];
  manageDirectory: boolean;
  grants: RoleGrant[];
};

const DEVICE_SCOPES: GrantScope[] = ["root", "org", "department", "tag"];

export const RECORDING_RULE_COPY =
  "recordings.rule at root or on the organisation changes these rules. devices.connect does not.";

export const CONNECT_RECORD_COPY =
  "devices.connect only ticks Record for one session when the effective rule is Allowed.";

export function permissionCopy(permission: string): string {
  if (permission === "recordings.rule") return RECORDING_RULE_COPY;
  if (permission === "devices.connect") return CONNECT_RECORD_COPY;
  if (permission === "recording.visible") return RECORDING_VISIBLE_HELP;
  return permission;
}

export const GRANT_PERMISSIONS: Array<{ name: string; scopes: GrantScope[] }> = [
  { name: "devices.read", scopes: DEVICE_SCOPES },
  { name: "devices.connect", scopes: DEVICE_SCOPES },
  { name: "devices.approve", scopes: DEVICE_SCOPES },
  { name: "devices.rename", scopes: DEVICE_SCOPES },
  { name: "devices.place", scopes: ["root", "org", "department"] },
  { name: "devices.tag", scopes: DEVICE_SCOPES },
  { name: "devices.revoke", scopes: DEVICE_SCOPES },
  { name: "devices.console", scopes: DEVICE_SCOPES },
  { name: "devices.delete", scopes: ["root"] },
  { name: "organisations.read", scopes: ["root", "org", "department"] },
  { name: "organisations.manage", scopes: ["root", "org"] },
  { name: "recordings.rule", scopes: ["root", "org"] },
  { name: "recordings.read", scopes: DEVICE_SCOPES },
  { name: "recording.visible", scopes: DEVICE_SCOPES },
  { name: "audit.read", scopes: DEVICE_SCOPES },
  { name: "directory.read", scopes: ["root"] },
  { name: "directory.manage", scopes: ["root"] },
  { name: "enrollment.manage", scopes: ["root"] },
  { name: "stealth.command", scopes: DEVICE_SCOPES },
  { name: "stealth.audit", scopes: ["root", "org"] },
];

export function rootGrants(names: string[]): RoleGrant[] {
  return names.map((permission) => ({ permission, scope: "root" }));
}

export function scopeWord(grants: RoleGrant[]): string {
  const kinds = new Set(grants.map((grant) => grant.scope));
  if (kinds.size === 0) return "none";
  if (kinds.size === 1) return [...kinds][0]!;
  return "mixed";
}

export function readsDirectory(access: MenuAccess | null): boolean {
  if (!access) return false;
  if (access.grants.some((grant) => grant.permission === "directory.read" || grant.permission === "directory.manage")) {
    return true;
  }
  return access.manageDirectory || access.roles.includes("Global Admin") || access.roles.includes("Global Supervisor");
}

export const RECORDING_VISIBLE_HELP =
  "Gates recording fields on device and session responses in this scope. Without it, the Record checkbox and recording status are omitted. A request with recording true while the rule is Off still returns 422 with detail Recording is not permitted. Allowed still stores the requested flag. Required still records. This permission does not change recordings.rule or recordings.read.";

export function readsRecording(access: MenuAccess | null): boolean {
  if (!access) return false;
  return access.grants.some((grant) => grant.permission === "recordings.rule");
}

export function readsAudit(access: MenuAccess | null | undefined): boolean {
  if (!access) return false;
  return access.grants.some((grant) => grant.permission === "audit.read");
}

export function canDeleteDevices(access: MenuAccess | null | undefined): boolean {
  if (!access) return false;
  return access.grants.some((grant) => grant.permission === "devices.delete" && grant.scope === "root");
}

/** Device delete API is off in prod; the portal hides delete actions there. */
export function portalDeviceDeleteEnabled(environment: "test" | "prod"): boolean {
  return environment !== "prod";
}

export type UserSource = "seeded" | "manual" | "scim";

export type DirectoryUser = {
  id: string;
  userName: string;
  displayName: string | null;
  disabled: boolean;
  seeded: boolean;
  source: UserSource;
  roles: string[];
};

export type DirectoryRole = {
  id: string;
  name: string;
  scope: string;
  builtIn: boolean;
  grants: RoleGrant[];
};

export type ScopeCatalog = {
  organisations: Array<{ id: string; name: string }>;
  departments: Array<{ id: string; organisationId: string; name: string }>;
  tags?: string[];
};

const OPERATOR_GRANT_NAMES = ["devices.read", "devices.connect", "organisations.read", "recordings.read", "recording.visible", "audit.read"];
const SUPERVISOR_GRANT_NAMES = GRANT_PERMISSIONS.map((item) => item.name).filter(
  (name) =>
    name !== "devices.connect" &&
    name !== "devices.delete" &&
    name !== "directory.manage" &&
    name !== "stealth.command" &&
    name !== "stealth.audit",
);

export const FIXTURE_ADMIN_ACCESS: MenuAccess = {
  roleLabel: "Global Admin",
  canConnect: "All endpoints",
  administer: true,
  connect: true,
  roles: ["Global Admin"],
  manageDirectory: true,
  grants: rootGrants(GRANT_PERMISSIONS.map((item) => item.name)),
};

export const FIXTURE_NO_ACCESS: MenuAccess = {
  roleLabel: "None",
  canConnect: "None",
  administer: false,
  connect: false,
  roles: [],
  manageDirectory: false,
  grants: [],
};

export const NO_ACCESS_MESSAGE = "You don't have access yet. Ask a Global Admin to give you a role.";

export const FIXTURE_OPERATOR_ACCESS: MenuAccess = {
  roleLabel: "Global Operator",
  canConnect: "All endpoints",
  administer: false,
  connect: true,
  roles: ["Global Operator"],
  manageDirectory: false,
  grants: rootGrants(OPERATOR_GRANT_NAMES),
};

export const FIXTURE_SUPERVISOR_ACCESS: MenuAccess = {
  roleLabel: "Global Supervisor",
  canConnect: "None",
  administer: true,
  connect: false,
  roles: ["Global Supervisor"],
  manageDirectory: false,
  grants: rootGrants(SUPERVISOR_GRANT_NAMES),
};

export const FIXTURE_ORG_ID = "11111111-1111-4111-8111-111111111111";
export const FIXTURE_DEPT_ID = "22222222-2222-4222-8222-222222222222";

export const FIXTURE_SCOPE_CATALOG: ScopeCatalog = {
  organisations: [{ id: FIXTURE_ORG_ID, name: "Wylo Retail" }],
  departments: [{ id: FIXTURE_DEPT_ID, organisationId: FIXTURE_ORG_ID, name: "Checkout" }],
  tags: ["site:leeds", "floor:north"],
};

const SEED_USER = "4e7a9c10-2b6d-4f83-a1e5-0d8c7b6a5f33";
const SAM_USER = "c3d4e5f6-a7b8-4c9d-8e0f-1a2b3c4d5e6f";
const NEIL_USER = "d7e8f901-2a3b-4c5d-8e6f-708192a3b4c5";
const SCIM_USER = "e8f90123-3b4c-4d5e-9f70-8192a3b4c5d6";

export const FIXTURE_USERS: DirectoryUser[] = [
  {
    id: SEED_USER,
    userName: "andy@wylosolutions.com",
    displayName: "Andy",
    disabled: false,
    seeded: true,
    source: "seeded",
    roles: ["Global Admin"],
  },
  {
    id: SAM_USER,
    userName: "sam.cole@wylo.example",
    displayName: "Sam",
    disabled: true,
    seeded: false,
    source: "manual",
    roles: ["Global Operator"],
  },
];

export const FIXTURE_STATUS_USERS: DirectoryUser[] = [
  {
    id: SEED_USER,
    userName: "andy@wylosolutions.com",
    displayName: "Andy",
    disabled: false,
    seeded: true,
    source: "seeded",
    roles: ["Global Admin"],
  },
  {
    id: NEIL_USER,
    userName: "neil@wylosolutions.com",
    displayName: "Neil",
    disabled: false,
    seeded: false,
    source: "manual",
    roles: ["Global Supervisor"],
  },
  {
    id: SCIM_USER,
    userName: "scim@wylosolutions.com",
    displayName: "SCIM",
    disabled: false,
    seeded: false,
    source: "scim",
    roles: ["Global Operator"],
  },
];

export const FIXTURE_ROLES: DirectoryRole[] = [
  {
    id: "5b9d1e20-4a8c-4d7f-b2e6-3f0a8c7d6e44",
    name: "Global Admin",
    scope: "root",
    builtIn: true,
    grants: rootGrants(GRANT_PERMISSIONS.map((item) => item.name)),
  },
  {
    id: "6f0e2a31-7c4b-4a1e-9d2f-1b8c0a5e4d11",
    name: "Global Operator",
    scope: "root",
    builtIn: true,
    grants: rootGrants(OPERATOR_GRANT_NAMES),
  },
  {
    id: "8a1c3b42-9d5e-4b2f-8e30-2c9d1b6f5e22",
    name: "Global Supervisor",
    scope: "root",
    builtIn: true,
    grants: rootGrants(SUPERVISOR_GRANT_NAMES),
  },
  {
    id: "9c2d4e53-1a6f-4c30-9f41-3d0e2c7a6f33",
    name: "Floor lead",
    scope: "department",
    builtIn: false,
    grants: [{ permission: "devices.read", scope: "department", departmentId: FIXTURE_DEPT_ID }],
  },
];

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function flag(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function userSource(value: unknown): UserSource | null {
  return value === "seeded" || value === "manual" || value === "scim" ? value : null;
}

function names(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) return null;
  return value;
}

export function parseMenuAccess(body: unknown): MenuAccess | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const roleLabel = text(record.roleLabel);
  const canConnect = text(record.canConnect);
  const administer = flag(record.administer);
  const connect = flag(record.connect);
  const roles = names(record.roles);
  if (!roleLabel || !canConnect || administer === null || connect === null || !roles) return null;
  const manageDirectory = flag(record.manageDirectory) ?? roles.includes("Global Admin");
  return { roleLabel, canConnect, administer, connect, roles, manageDirectory, grants: readGrants(record.grants) };
}

export function parseDirectoryUser(body: unknown): DirectoryUser | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const id = text(record.id);
  const userName = text(record.userName);
  const disabled = flag(record.disabled);
  const seeded = flag(record.seeded);
  const source = userSource(record.source);
  const roles = names(record.roles);
  if (!id || !userName || disabled === null || seeded === null || source === null || !roles) return null;
  const display = record.displayName;
  const displayName = display == null ? null : text(display);
  if (display != null && displayName === null) return null;
  return { id, userName, displayName, disabled, seeded, source, roles };
}

export function parseDirectoryRole(body: unknown): DirectoryRole | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const id = text(record.id);
  const name = text(record.name);
  const scope = text(record.scope);
  const builtIn = flag(record.builtIn);
  if (!id || !name || !scope || builtIn === null) return null;
  return { id, name, scope, builtIn, grants: readGrants(record.grants) };
}

function readGrants(value: unknown): RoleGrant[] {
  if (!Array.isArray(value)) return [];
  const grants: RoleGrant[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") return [];
    const record = item as Record<string, unknown>;
    const permission = text(record.permission);
    const scope = text(record.scope);
    if (!permission || (scope !== "root" && scope !== "org" && scope !== "department" && scope !== "tag")) return [];
    const grant: RoleGrant = { permission, scope };
    if (typeof record.organisationId === "string") grant.organisationId = record.organisationId;
    if (typeof record.departmentId === "string") grant.departmentId = record.departmentId;
    if (typeof record.tagKey === "string") grant.tagKey = record.tagKey;
    if (typeof record.tagValue === "string") grant.tagValue = record.tagValue;
    grants.push(grant);
  }
  return grants;
}

async function bearer(apiBase: string, path: string, token: () => Promise<string>, signal: AbortSignal): Promise<Response> {
  const accessToken = await token();
  const response = await fetch(new URL(path, apiBase), {
    headers: { authorization: `Bearer ${accessToken}`, accept: "application/json" },
    signal,
  });
  await rejectIfStepUp(response);
  return response;
}

export async function loadMenuAccess(
  apiBase: string,
  token: () => Promise<string>,
  signal: AbortSignal,
): Promise<MenuAccess | null> {
  try {
    const response = await bearer(apiBase, "/v1/access", token, signal);
    if (!response.ok) return null;
    return parseMenuAccess(await response.json());
  } catch (cause) {
    rethrowStepUp(cause);
    return null;
  }
}

export async function loadDirectoryUsers(
  apiBase: string,
  token: () => Promise<string>,
  signal: AbortSignal,
): Promise<DirectoryUser[] | null> {
  try {
    const response = await bearer(apiBase, "/v1/admin/users", token, signal);
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!Array.isArray(body)) return null;
    const users = body.map(parseDirectoryUser);
    if (users.some((user) => user === null)) return null;
    return users as DirectoryUser[];
  } catch (cause) {
    rethrowStepUp(cause);
    return null;
  }
}

export async function loadDirectoryRoles(
  apiBase: string,
  token: () => Promise<string>,
  signal: AbortSignal,
): Promise<DirectoryRole[] | null> {
  try {
    const response = await bearer(apiBase, "/v1/admin/roles", token, signal);
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!Array.isArray(body)) return null;
    const roles = body.map(parseDirectoryRole);
    if (roles.some((role) => role === null)) return null;
    return roles as DirectoryRole[];
  } catch (cause) {
    rethrowStepUp(cause);
    return null;
  }
}

async function writeUser(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body?: unknown,
): Promise<DirectoryUser | "removed" | null> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(path, apiBase), {
      method,
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return null;
    if (method === "DELETE") return "removed";
    return parseDirectoryUser(await response.json());
  } catch (cause) {
    rethrowStepUp(cause);
    return null;
  }
}

export function createDirectoryUser(
  apiBase: string,
  token: () => Promise<string>,
  input: { userName: string; displayName: string; roles: string[] },
): Promise<DirectoryUser | null> {
  const saved = writeUser(apiBase, token, "/v1/admin/users", "POST", {
    userName: input.userName,
    displayName: input.displayName.length > 0 ? input.displayName : null,
    roles: input.roles,
  });
  return saved.then((user) => (user && user !== "removed" ? user : null));
}

export function updateDirectoryUser(
  apiBase: string,
  token: () => Promise<string>,
  userId: string,
  input: { userName: string; displayName: string; roles: string[]; disabled: boolean },
): Promise<DirectoryUser | null> {
  const saved = writeUser(apiBase, token, `/v1/admin/users/${userId}`, "PUT", {
    userName: input.userName,
    displayName: input.displayName.length > 0 ? input.displayName : null,
    roles: input.roles,
    disabled: input.disabled,
  });
  return saved.then((user) => (user && user !== "removed" ? user : null));
}

export function setDirectoryRoles(
  apiBase: string,
  token: () => Promise<string>,
  userId: string,
  roles: string[],
): Promise<DirectoryUser | null> {
  return setDirectoryUserRoles(apiBase, token, userId, roles).then((result) => result.user);
}

export function setDirectoryUserRoles(
  apiBase: string,
  token: () => Promise<string>,
  userId: string,
  roles: string[],
): Promise<{ user: DirectoryUser | null; detail: string | null }> {
  return writeUserRoles(apiBase, token, `/v1/admin/users/${userId}/roles`, { roles });
}

async function writeUserRoles(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  body: { roles: string[] },
): Promise<{ user: DirectoryUser | null; detail: string | null }> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(path, apiBase), {
      method: "PUT",
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return { user: null, detail: await problemDetail(response) };
    return { user: parseDirectoryUser(await response.json()), detail: null };
  } catch (cause) {
    rethrowStepUp(cause);
    return { user: null, detail: "That user could not be updated." };
  }
}

export function deleteDirectoryUser(
  apiBase: string,
  token: () => Promise<string>,
  userId: string,
): Promise<boolean> {
  return writeUser(apiBase, token, `/v1/admin/users/${userId}`, "DELETE").then((user) => user === "removed");
}

export function createDirectoryRole(
  apiBase: string,
  token: () => Promise<string>,
  input: { name: string; grants: RoleGrant[] },
): Promise<{ role: DirectoryRole | null; detail: string | null }> {
  return writeRole(apiBase, token, "/v1/admin/roles", "POST", input);
}

export function updateDirectoryRole(
  apiBase: string,
  token: () => Promise<string>,
  roleId: string,
  input: { name: string; grants: RoleGrant[] },
): Promise<{ role: DirectoryRole | null; detail: string | null }> {
  return writeRole(apiBase, token, `/v1/admin/roles/${roleId}`, "PUT", input);
}

export async function loadScopeCatalog(
  apiBase: string,
  token: () => Promise<string>,
  signal: AbortSignal,
): Promise<ScopeCatalog> {
  const empty = { organisations: [], departments: [], tags: [] as string[] };
  try {
    const accessToken = await token();
    const headers = { authorization: `Bearer ${accessToken}`, accept: "application/json" };
    const response = await fetch(new URL("/v1/organisations", apiBase), { headers, signal });
    await rejectIfStepUp(response);
    if (!response.ok) return empty;
    const body: unknown = await response.json();
    if (!Array.isArray(body)) return empty;
    const organisations = body.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const record = item as Record<string, unknown>;
      const id = text(record.organisationId);
      const name = text(record.name);
      return id && name ? [{ id, name }] : [];
    });
    const departments: ScopeCatalog["departments"] = [];
    for (const organisation of organisations) {
      const departmentsResponse = await fetch(new URL(`/v1/organisations/${organisation.id}/departments`, apiBase), {
        headers,
        signal,
      });
      await rejectIfStepUp(departmentsResponse);
      if (!departmentsResponse.ok) continue;
      const rows: unknown = await departmentsResponse.json();
      if (!Array.isArray(rows)) continue;
      for (const row of rows) {
        if (!row || typeof row !== "object") continue;
        const record = row as Record<string, unknown>;
        const id = text(record.departmentId);
        const name = text(record.name);
        if (id && name) departments.push({ id, organisationId: organisation.id, name });
      }
    }
    return { organisations, departments, tags: await loadCatalogTags(apiBase, headers, signal) };
  } catch (cause) {
    rethrowStepUp(cause);
    return empty;
  }
}

async function loadCatalogTags(apiBase: string, headers: HeadersInit, signal: AbortSignal): Promise<string[]> {
  const seen = new Set<string>();
  const tags: string[] = [];
  try {
    return await readCatalogTags(apiBase, headers, signal, seen, tags);
  } catch (cause) {
    rethrowStepUp(cause);
    return tags;
  }
}

async function readCatalogTags(
  apiBase: string,
  headers: HeadersInit,
  signal: AbortSignal,
  seen: Set<string>,
  tags: string[],
): Promise<string[]> {
  let cursor: string | null = null;
  for (let page = 0; page < 5; page += 1) {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    const query = params.toString();
    const response = await fetch(new URL(query.length === 0 ? "/v1/inventory/devices" : `/v1/inventory/devices?${query}`, apiBase), {
      headers,
      signal,
    });
    await rejectIfStepUp(response);
    if (!response.ok) return tags;
    const body: unknown = await response.json();
    if (!body || typeof body !== "object") return tags;
    const record = body as Record<string, unknown>;
    const items = Array.isArray(record.items) ? record.items : [];
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const tagList = (item as Record<string, unknown>).tags;
      if (!Array.isArray(tagList)) continue;
      for (const tag of tagList) {
        if (!tag || typeof tag !== "object") continue;
        const row = tag as Record<string, unknown>;
        const key = text(row.key);
        const value = text(row.value) ?? "";
        if (!key) continue;
        const label = value.length === 0 ? key : `${key}:${value}`;
        const fold = label.toLowerCase();
        if (seen.has(fold)) continue;
        seen.add(fold);
        tags.push(label);
      }
    }
    cursor = typeof record.nextCursor === "string" && record.nextCursor.length > 0 ? record.nextCursor : null;
    if (!cursor) break;
  }
  return tags;
}

export function deleteDirectoryRole(
  apiBase: string,
  token: () => Promise<string>,
  roleId: string,
): Promise<{ removed: boolean; detail: string | null }> {
  return writeRole(apiBase, token, `/v1/admin/roles/${roleId}`, "DELETE").then((result) => ({
    removed: result.removed,
    detail: result.detail,
  }));
}

async function writeRole(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body?: unknown,
): Promise<{ role: DirectoryRole | null; removed: boolean; detail: string | null }> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(path, apiBase), {
      method,
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return { role: null, removed: false, detail: await problemDetail(response) };
    if (method === "DELETE") return { role: null, removed: true, detail: null };
    return { role: parseDirectoryRole(await response.json()), removed: false, detail: null };
  } catch (cause) {
    rethrowStepUp(cause);
    return { role: null, removed: false, detail: "That role could not be saved." };
  }
}

async function problemDetail(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "detail" in body && typeof body.detail === "string" && body.detail.length > 0) {
      return body.detail;
    }
  } catch {
    /* problem bodies are JSON */
  }
  return "That change could not be saved.";
}

export type DepartmentRecordingSetting = {
  departmentId: string;
  name: string;
  recordingRule: string;
  effectiveRule: string;
  locked: boolean;
  stealthCommand: boolean;
  reasonRule: string;
  reasonEffective: string;
  reasonLocked: boolean;
};

export type TagRecordingSetting = {
  key: string;
  value: string;
  recordingRule: string;
  effectiveRule: string;
  locked: boolean;
  stealthCommand: boolean;
  reasonRule: string;
  reasonEffective: string;
  reasonLocked: boolean;
};

export type OrganisationRecordingSetting = {
  organisationId: string;
  name: string;
  recordingRule: string;
  stealthCommand: boolean;
  reasonRule: string;
  reasonLocked: boolean;
  departments: DepartmentRecordingSetting[];
  tags: TagRecordingSetting[];
};

const ORG_RULES = ["Off", "Allowed", "Required"];
const DEPT_RULES = ["Inherit", "Off", "Allowed", "Required"];
const REASON_ORG = ["Off", "Required"];
const REASON_CHILD = ["Inherit", "Off", "Required"];

function oneOf(value: unknown, allowed: string[]): string | null {
  return typeof value === "string" && allowed.includes(value) ? value : null;
}

export async function loadRecordingSettings(
  apiBase: string,
  token: () => Promise<string>,
  signal: AbortSignal,
): Promise<{ rows: OrganisationRecordingSetting[]; error: string | null }> {
  try {
    const accessToken = await token();
    const headers = { authorization: `Bearer ${accessToken}`, accept: "application/json" };
    const response = await fetch(new URL("/v1/groups", apiBase), { headers, signal });
    await rejectIfStepUp(response);
    if (!response.ok) return { rows: [], error: await problemDetail(response) };
    const body: unknown = await response.json();
    if (!Array.isArray(body)) return { rows: [], error: "Recordings could not be loaded." };
    const rows: OrganisationRecordingSetting[] = [];
    for (const item of body) {
      const parsed = parseGroup(item);
      if (parsed) rows.push(parsed);
    }
    return { rows, error: null };
  } catch (cause) {
    rethrowStepUp(cause);
    return { rows: [], error: "Recordings could not be loaded." };
  }
}

function parseGroup(item: unknown): OrganisationRecordingSetting | null {
  if (!item || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;
  const organisationId = text(record.organisationId);
  const name = text(record.name);
  const recordingRule = oneOf(record.recordingRule, ORG_RULES);
  if (!organisationId || !name || !recordingRule || !Array.isArray(record.departments) || !Array.isArray(record.tags)) return null;
  const departments: DepartmentRecordingSetting[] = [];
  for (const row of record.departments) {
    if (!row || typeof row !== "object") continue;
    const department = row as Record<string, unknown>;
    const departmentId = text(department.departmentId);
    const departmentName = text(department.name);
    const choice = oneOf(department.recordingRule, DEPT_RULES);
    const effective = oneOf(department.effectiveRule, ORG_RULES);
    if (!departmentId || !departmentName || !choice || !effective || typeof department.locked !== "boolean") continue;
    departments.push({
      departmentId,
      name: departmentName,
      recordingRule: choice,
      effectiveRule: effective,
      locked: department.locked,
      stealthCommand: false,
      reasonRule: oneOf(department.reasonRule, REASON_CHILD) ?? "Inherit",
      reasonEffective: oneOf(department.reasonEffective, REASON_ORG) ?? "Off",
      reasonLocked: department.reasonLocked === true,
    });
  }
  const tags: TagRecordingSetting[] = [];
  for (const row of record.tags) {
    if (!row || typeof row !== "object") continue;
    const tag = row as Record<string, unknown>;
    const key = text(tag.key);
    const value = typeof tag.value === "string" ? tag.value : null;
    const choice = oneOf(tag.recordingRule, DEPT_RULES);
    const effective = oneOf(tag.effectiveRule, ORG_RULES);
    if (!key || value === null || !choice || !effective || typeof tag.locked !== "boolean") continue;
    tags.push({
      key,
      value,
      recordingRule: choice,
      effectiveRule: effective,
      locked: tag.locked,
      stealthCommand: false,
      reasonRule: oneOf(tag.reasonRule, REASON_CHILD) ?? "Inherit",
      reasonEffective: oneOf(tag.reasonEffective, REASON_ORG) ?? "Off",
      reasonLocked: tag.reasonLocked === true,
    });
  }
  return {
    organisationId,
    name,
    recordingRule,
    stealthCommand: false,
    reasonRule: oneOf(record.reasonRule, REASON_ORG) ?? "Off",
    reasonLocked: record.reasonLocked === true,
    departments,
    tags,
  };
}

export async function putOrganisationRecordingRule(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  recordingRule: string,
): Promise<string | null> {
  return putRecordingRule(apiBase, token, `/v1/organisations/${organisationId}/recording-rule`, recordingRule);
}

export async function putDepartmentRecordingRule(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  departmentId: string,
  recordingRule: string,
): Promise<string | null> {
  return putRecordingRule(
    apiBase,
    token,
    `/v1/organisations/${organisationId}/departments/${departmentId}/recording-rule`,
    recordingRule,
  );
}

export async function putTagRecordingRule(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  key: string,
  value: string,
  recordingRule: string,
): Promise<string | null> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(`/v1/organisations/${organisationId}/tags/recording-rule`, apiBase), {
      method: "PUT",
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ key, value, recordingRule }),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return problemDetail(response);
    return null;
  } catch (cause) {
    rethrowStepUp(cause);
    return "That change could not be saved.";
  }
}

export async function putOrganisationReason(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  reasonRequired: string,
): Promise<string | null> {
  return putJson(apiBase, token, `/v1/organisations/${organisationId}/reason-policy`, { reasonRequired });
}

export async function putDepartmentReason(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  departmentId: string,
  reasonRequired: string,
): Promise<string | null> {
  return putJson(apiBase, token, `/v1/organisations/${organisationId}/departments/${departmentId}/reason-policy`, { reasonRequired });
}

export async function putTagReason(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  key: string,
  value: string,
  reasonRequired: string,
): Promise<string | null> {
  return putJson(apiBase, token, `/v1/organisations/${organisationId}/tags/reason-policy`, { key, value, reasonRequired });
}

async function putJson(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  body: unknown,
): Promise<string | null> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(path, apiBase), {
      method: "PUT",
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return problemDetail(response);
    return null;
  } catch (cause) {
    rethrowStepUp(cause);
    return "That change could not be saved.";
  }
}

async function putRecordingRule(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  recordingRule: string,
): Promise<string | null> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(path, apiBase), {
      method: "PUT",
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ recordingRule }),
    });
    await rejectIfStepUp(response);
    if (!response.ok) return problemDetail(response);
    return null;
  } catch (cause) {
    rethrowStepUp(cause);
    return "That change could not be saved.";
  }
}

export async function setDirectoryDisabled(
  apiBase: string,
  token: () => Promise<string>,
  userId: string,
  disabled: boolean,
): Promise<boolean> {
  try {
    const accessToken = await token();
    const response = await fetch(new URL(`/v1/admin/users/${userId}/disabled`, apiBase), {
      method: "PUT",
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ disabled }),
    });
    await rejectIfStepUp(response);
    return response.ok;
  } catch (cause) {
    rethrowStepUp(cause);
    return false;
  }
}
